import React, { ReactElement } from "react"
import { Button, Stack, TableCell, TableRow, Typography } from "@carrotfertility/carotene-core"
import Moment from "moment"
import { CarrotPlan } from "../../types/CarrotPlan"
import { RoutingInfo } from "types/RoutingInfo"
import { getRequestSourceDisplayName } from "utils/RequestSourceOptions"
import { isCheckInIntake } from "./utils/isCheckInIntake"
import { convertFromUtcToLocal } from "../../utils/convertFromUtcToLocal"
import { PlanTags } from "./PlanTags"

interface JourneyDisplayProps {
  journey: string
  journeyStage?: string
}

const JourneyDisplay = ({ journey, journeyStage }: JourneyDisplayProps): JSX.Element => {
  if (!journey) {
    return null
  }

  return (
    <Stack direction="column" gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
      <Typography>{journey}</Typography>
      {journeyStage && <Typography fontStyle={"italic"}>{journeyStage}</Typography>}
    </Stack>
  )
}

const ViewButton = (props: React.PropsWithChildren<React.ComponentProps<typeof Button>>): JSX.Element => (
  <Button sx={{ whiteSpace: "nowrap" }} variant={"outlined"} color={"secondary"} size={"small"} {...props}>
    {props.children}
  </Button>
)

interface PlanEntryProps {
  employeeId: number
  plan: CarrotPlan
  matchingRoutingInfo: RoutingInfo
}

export const PlanEntry = ({ employeeId, plan, matchingRoutingInfo }: PlanEntryProps): ReactElement => {
  const isCarrotPlanFromMemberCheckin = isCheckInIntake(plan?.requestSource)
  const isSensitiveScenario = matchingRoutingInfo?.isStillInPrevJourneyStage === "SENSITIVE_SCENARIO"
  const requestSourceDisplayName = getRequestSourceDisplayName(plan.requestSource)
  const isRepublished = !!plan.sourceCarrotPlanId

  const [planJourney, planJourneyStage] = [
    plan.journeys[0]?.journeyDisplayName,
    plan.journeys[0]?.journeyStageDisplayName
  ]

  return (
    <TableRow>
      <TableCell aria-details={"date"}>{Moment(plan.createdAtUtc).format("MMM D, YYYY")}</TableCell>
      <TableCell aria-details={"source"}>{requestSourceDisplayName}</TableCell>
      <TableCell aria-details={"published-by"}>{plan.createdByUsername}</TableCell>
      <TableCell aria-details={"tags"}>
        <PlanTags
          journeyChanged={plan.journeyChanged}
          isCheckedIn={isCarrotPlanFromMemberCheckin}
          isSensitiveScenario={isSensitiveScenario}
          isRepublished={isRepublished}
        />
      </TableCell>
      <TableCell>
        <JourneyDisplay journey={planJourney} journeyStage={planJourneyStage} />
      </TableCell>
      <TableCell aria-details={"links"} align="right">
        <ViewButton href={`/employees/${employeeId}/carrot-plans/${plan.carrotPlanId}`}>View Carrot Plan</ViewButton>
      </TableCell>
    </TableRow>
  )
}

interface RoutingAnswersProps {
  employeeId: number
  routingInfo: RoutingInfo
  allMedicalQuestionsSkipped: boolean
}

export const RoutingAnswers = ({
  employeeId,
  routingInfo,
  allMedicalQuestionsSkipped
}: RoutingAnswersProps): ReactElement => {
  const routingCompletedDate = routingInfo?.routingCompletedDate
  const isRoutingInfoFromMemberCheckIn = isCheckInIntake(routingInfo?.requestSource)
  const isSensitiveScenario = routingInfo?.isStillInPrevJourneyStage === "SENSITIVE_SCENARIO"
  const isRequestNewerThanActiveCarrotPlan = routingInfo?.isRequestNewerThanActiveCarrotPlan
  const isNoCarrotPlanNeeded = routingInfo?.sensitiveScenarioSupport === "NO_CARROT_PLAN_NEEDED"
  const isCompleteRoutingRequest = routingCompletedDate !== null

  const [journeyDisplayName, journeyStageDisplayName] = [
    routingInfo?.journeyDisplayName,
    routingInfo?.journeyStageDisplayName
  ]

  return (
    <TableRow>
      <TableCell aria-details={"date"}>
        <Typography>{"Member's latest routing answers"}</Typography>
        <Typography variant={"body2"} fontStyle={"italic"}>
          {isCompleteRoutingRequest
            ? `(last updated: ${convertFromUtcToLocal(routingCompletedDate)})`
            : "(currently in progress)"}
        </Typography>
      </TableCell>
      <TableCell aria-details={"source"}>{""}</TableCell>
      <TableCell aria-details={"published-by"}>{""}</TableCell>
      <TableCell aria-details={"tags"}>
        {isCompleteRoutingRequest && (
          <PlanTags
            journeyChanged={routingInfo?.journeyChanged}
            isCheckedIn={isRoutingInfoFromMemberCheckIn}
            isSensitiveScenario={isSensitiveScenario}
            allMedicalQuestionsSkipped={allMedicalQuestionsSkipped}
            isRequestNewerThanActiveCarrotPlan={isRequestNewerThanActiveCarrotPlan}
            isNoCarrotPlanNeeded={isNoCarrotPlanNeeded}
          />
        )}
      </TableCell>
      <TableCell>
        <JourneyDisplay journey={journeyDisplayName} journeyStage={journeyStageDisplayName} />
      </TableCell>
      <TableCell aria-details={"links"} align="right">
        <ViewButton href={`/employees/${employeeId}/carrot-plans/routing-info`}>View routing answers</ViewButton>
      </TableCell>
    </TableRow>
  )
}
